import {  Text, View, Pressable, Keyboard  } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSharedValue } from 'react-native-reanimated';
import { KeyboardChatScrollView, KeyboardController, KeyboardGestureArea, KeyboardStickyView } from 'react-native-keyboard-controller';
import ChatBubble from '@/components/Connect/Conversations/ChatBubble';
import MessageInput from '@/components/Connect/Conversations/MessageInput';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Avatar from '@/components/People/Avatar';
import { addMessageReaction, createMessage, removeMessageReaction, updateMessage, deleteConversation, toggleConversationMute } from '@/data/connect';
import { createNotification } from '@/data/notifications';
import useMarkNotificationsRead from '@/utils/hooks/useMarkNotificationsRead';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { ConnectedReactionMenu } from '@/components/Connect/ReactionMenu';
import ReplyContainer from '@/components/Connect/Conversations/ReplyContainer';
import EditContainer from '@/components/Connect/Conversations/EditContainer';
import { LegendList } from "@legendapp/list";
import { supabase } from '@/data/client';
import useMessagesStore from '@/data/store/useMessagesStore';
import usePlatform from '@/utils/hooks/usePlatform';
import * as Crypto from 'expo-crypto';
import { useFocusEffect, useLocalSearchParams, useRouter, useNavigation, useIsFocused } from 'expo-router';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import Unicon from '@/components/General/Unicon';
import DropdownMenu from '@/components/General/DropdownMenu';
import { showAlert } from '@/utils/showAlert';
import SplitView from '@/components/General/SplitView';
import ConversationsList from '@/components/Connect/Conversations/ConversationsList';
import { stripMentions, extractMentions } from '@/utils/mentionFormat';
import useSaveShortcut from '@/utils/hooks/useSaveShortcut';
import { saveToRecents } from '@/utils/recentlyVisited';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import AdminViewingBanner from '@/components/General/AdminViewingBanner';



const VirtualizedListScrollView = ((props, ref) => {
  return (
    <KeyboardChatScrollView
      ref={ref}
      automaticallyAdjustContentInsets={false}
      contentInsetAdjustmentBehavior="never"
      keyboardDismissMode="interactive"
      {...props}
    />
  );
});

const MessagesScreen = () => {
    const AVATAR_SIZE = 35;
    const AVATAR_STACK_OFFSET = 20;
    
    const replyingToMessage = useMessagesStore(state => state.replyingToMessage);
    const editingMessage = useMessagesStore(state => state.editingMessage);
    const inputRef = useMessagesStore(state => state.inputRef);
    const setReplyingToMessage = useMessagesStore(state => state.setReplyingToMessage);
    const setEditingMessage = useMessagesStore(state => state.setEditingMessage);
    const setHighlightedMessageID = useMessagesStore(state => state.setHighlightedMessageID);
    const setScrollToMessageCallback = useMessagesStore(state => state.setScrollToMessageCallback);
    const { conversationID } = useLocalSearchParams()
    const queryClient = useQueryClient();
    const router = useRouter();
    const navigation = useNavigation();
    const { isNative } = usePlatform();
    const isFocused = useIsFocused();

    //isPending (not isLoading) — during persisted-cache hydration queries are paused
    //(fetchStatus idle) so isLoading can be false while data is still undefined.
    const { data: loggedInUser, isPending: loggedInUserLoading } = Query.people.loggedInUser()

    const { data: messages, isLoading: fetchingMessages, isFetchingNextPage, hasNextPage, fetchNextPage, refetch: refetchMessages } = Query.messages.byConversationId({conversationID})
    const { data: conversation, isPending: conversationLoading, refetch: refetchConversation } = Query.conversations.byId({id: conversationID})

    //Refs so the realtime channel callbacks never work with stale closures
    const loggedInUserRef = useRef(loggedInUser);
    loggedInUserRef.current = loggedInUser;
    const conversationRef = useRef(conversation);
    conversationRef.current = conversation;

    const peopleById = useMemo(() => {
        const map = new Map();
        (conversation?.People || []).forEach(p => {
            if(p?.id) map.set(p.id, p);
        });
        return map;
    }, [conversation?.People]);

    const flatData = useMemo(() => {
        const all = messages?.pages.flatMap(page => page.data) || [];
        //Dedupe by id — offset-based pagination can return the same message in two pages
        //if a new message was inserted between page fetches.
        const seen = new Set();
        return all.map(m => {
            if(!m?.id || seen.has(m.id)) return null;
            seen.add(m.id);

            const needsSender = (s) => !s || !s.id || !s.firstName || !s.lastName;
            let normalized = m;
            if(needsSender(m.Sender) && m.senderID && peopleById.has(m.senderID)){
                normalized = {...m, Sender: {...(m.Sender || {}), ...peopleById.get(m.senderID)}};
            }

            if(normalized.repliedTo && needsSender(normalized.repliedTo.Sender) && normalized.repliedTo.senderID && peopleById.has(normalized.repliedTo.senderID)){
                normalized = {
                    ...normalized,
                    repliedTo: {
                        ...normalized.repliedTo,
                        Sender: {...(normalized.repliedTo.Sender || {}), ...peopleById.get(normalized.repliedTo.senderID)}
                    }
                };
            }

            return normalized;
        }).filter(Boolean).reverse();
    }, [messages, peopleById]);

    //Stable reference — a fresh array every render would re-render all visible
    //bubbles on every useMessagesStore write.
    const listData = useMemo(() => (fetchingMessages ? [] : [...(flatData || [])]), [fetchingMessages, flatData]);

    const listRef = useRef(null);
    const [isReady, setIsReady] = useState(false);
    const [isScrollingToMessage, setIsScrollingToMessage] = useState(false);
    const [adminBannerDismissed, setAdminBannerDismissed] = useState(false);
    const isScrollingToMessageRef = useRef(false);
    useEffect(() => {
        setIsReady(false);
    }, [conversationID])

    //When data loads: scroll to bottom while still hidden, THEN reveal.
    //Scrolling repeatedly across several frames (instead of a single timed guess)
    //accounts for layout/content (images, attachments) still settling.
    //Only runs on initial load (when isReady is false) — NOT on refetches, which would
    //scroll past the KeyboardChatScrollView's contentInset and hide messages behind the keyboard.
    useEffect(() => {
        if(!fetchingMessages && flatData?.length > 0 && !isReady){
            let cancelled = false;
            let frame = 0;
            const maxFrames = isNative ? 4 : 3;

            const scrollToBottom = () => {
                if(isNative){
                    listRef.current?.scrollToEnd?.({animated: false});
                } else {
                    //On web, index-based scrolling relies on estimated item sizes and
                    //can undershoot. scrollToOffset with a huge value gets clamped by
                    //the browser to the real max scroll, landing exactly at the bottom.
                    listRef.current?.scrollToOffset?.({offset: Number.MAX_SAFE_INTEGER, animated: false});
                }
            };

            const tick = () => {
                if(cancelled) return;
                scrollToBottom();
                frame++;
                if(frame < maxFrames){
                    requestAnimationFrame(tick);
                } else {
                    setIsReady(true);
                    //Late-loading images/attachments can grow content after reveal — nudge again
                    if(!isNative){
                        setTimeout(scrollToBottom, 50);
                        setTimeout(scrollToBottom, 150);
                    } else {
                        //Native: same idea but later — image/attachment layout can settle
                        //well after reveal. The isScrollingToMessageRef guard avoids
                        //fighting an in-flight scroll-to-message jump.
                        const nudge = () => {
                            if(isScrollingToMessageRef.current) return;
                            listRef.current?.scrollToEnd?.({animated: false});
                        };
                        setTimeout(nudge, 300);
                        setTimeout(nudge, 800);
                    }
                }
            };

            requestAnimationFrame(tick);
            return () => { cancelled = true; };
        } else if(!fetchingMessages && flatData?.length === 0 && !isReady){
            //No messages — just reveal immediately
            setIsReady(true);
        }
    }, [fetchingMessages, flatData, isReady])

    //Scroll to end on new message on web — LegendList's maintainScrollAtEnd already
    //handles this on native (it's explicitly gated to isNative below), so only run
    //this manually for web where maintainScrollAtEnd is disabled.
    const prevDataLengthRef = useRef(0);
    useEffect(() => {
        const currentLength = flatData?.length || 0;
        if(!isNative && currentLength > prevDataLengthRef.current && isReady){
            requestAnimationFrame(() => {
                listRef.current?.scrollToOffset?.({offset: Number.MAX_SAFE_INTEGER, animated: false});
            });
        }
        prevDataLengthRef.current = currentLength;
    }, [flatData?.length, isReady, isNative]);

    //Scroll-to-message: finds the message in loaded data and scrolls to it.
    //If the message isn't loaded yet, fetches older pages until it appears.
    const flatDataRef = useRef(flatData);
    flatDataRef.current = flatData;
    const hasNextPageRef = useRef(hasNextPage);
    hasNextPageRef.current = hasNextPage;
    const isFetchingNextPageRef = useRef(isFetchingNextPage);
    isFetchingNextPageRef.current = isFetchingNextPage;
    const fetchNextPageRef = useRef(fetchNextPage);
    fetchNextPageRef.current = fetchNextPage;

    const scrollToMessage = useCallback(async (messageID) => {
        if(!messageID) return;

        const findAndScroll = () => {
            const data = flatDataRef.current;
            if(!data) return false;
            const index = data.findIndex(m => m?.id === messageID);
            if(index >= 0){
                listRef.current?.scrollToIndex?.({index, animated: false, viewPosition: 0.5});
                setHighlightedMessageID(messageID);
                setTimeout(() => setHighlightedMessageID(undefined), 2500);
                return true;
            }
            return false;
        };

        //Always disable maintain-scroll props during scroll-to-message to prevent
        //maintainScrollAtEnd / maintainVisibleContentPosition from fighting the jump
        isScrollingToMessageRef.current = true;
        setIsScrollingToMessage(true);

        try {
            //Try scrolling immediately if the message is already loaded
            if(findAndScroll()) return;

            //Message not in loaded data — fetch pages until it appears
            const maxAttempts = 10;
            for(let i = 0; i < maxAttempts; i++){
                if(!hasNextPageRef.current || isFetchingNextPageRef.current){
                    if(!hasNextPageRef.current) break;
                    await new Promise(r => setTimeout(r, 500));
                    continue;
                }

                await fetchNextPageRef.current?.();
                //Wait for the new data to be committed and layout to settle
                await new Promise(r => setTimeout(r, 800));

                if(findAndScroll()) break;
            }
        } finally {
            //Re-enable maintain-scroll after a delay so the scroll position settles
            setTimeout(() => {
                isScrollingToMessageRef.current = false;
                setIsScrollingToMessage(false);
            }, 500);
        }
    }, []);

    useEffect(() => {
        setScrollToMessageCallback(scrollToMessage);
    }, [scrollToMessage]);

    const refreshConversation = useCallback(async () => {
        await Promise.all([
            refetchMessages(),
            refetchConversation(),
            queryClient.invalidateQueries({queryKey: QueryKeys.conversations.byUserId(loggedInUserRef.current?.id)}),
            queryClient.invalidateQueries({queryKey: QueryKeys.people.loggedInUser()}),
        ]);
    }, [refetchMessages, refetchConversation]);

    useFocusEffect(useCallback(() => {
        if(conversationID) refreshConversation();
        return () => {
            Keyboard.dismiss();
            //Keyboard.dismiss() is a no-op on web — blur the input directly
            inputRef?.current?.blur?.();
        };
    }, [conversationID, refreshConversation]));

    //Dismiss the keyboard BEFORE the screen pops. The useFocusEffect cleanup above fires
    //as focus shifts, by which point react-native-screens has frozen this screen and the
    //blur/dismiss can't reach the native TextInput — so the keyboard stays stuck on back.
    //beforeRemove fires synchronously before the transition begins.
    //Use KeyboardController.dismiss (native module) rather than RN's Keyboard.dismiss —
    //the latter races with the interactive dismiss / KeyboardGestureArea and only works
    //intermittently (stuck on first back, closes on second). keepFocus:false also blurs.
    useEffect(() => {
        const unsub = navigation.addListener('beforeRemove', () => {
            KeyboardController.dismiss({ keepFocus: false });
            inputRef?.current?.blur?.();
        });
        return unsub;
    }, [navigation, inputRef]);

    const updateMessageMutation = useMutation({
        mutationFn: updateMessage,
        onMutate: async (data) => {
            updateMessageInQueryCache(data.details)
        },
        onSuccess: (data) => {
            //Apply the server-returned row (includes trigger-set editedAt) so the
            //sender sees the "Edited" indicator without a reload. Realtime UPDATE
            //skips the sender's own messages, so this is the only path that does it.
            if(data?.id){
                updateMessageInQueryCache(data)
            }
        },
    })

    const addReactionMutation = useMutation({
        mutationFn: addMessageReaction,
        onMutate: (data) => {
            if(!data) return;
            addReactionToQueryCache(data)
        }
    })

    const removeReactionMutation = useMutation({
        mutationFn: removeMessageReaction,
        onMutate: (data) => {
            if(!data) return; 
            removeReactionFromQueryCache(data)
        }
    })

    const toggleMuteMutation = useMutation({
        mutationFn: toggleConversationMute,
        onSuccess: (data) => {
            queryClient.setQueryData(QueryKeys.conversations.byId(conversationID), old => {
                if(!old || Array.isArray(old)) return old;
                return {
                    ...old,
                    People: old.People?.map(p => p.id == data?.personID ? {...p, isMuted: data?.isMuted} : p),
                };
            });
            queryClient.invalidateQueries({queryKey: QueryKeys.conversations.byUserId(loggedInUser?.id)});
        }
    })

    const leaveConversationMutation = useMutation({
        mutationFn: deleteConversation,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: QueryKeys.conversations.byUserId(loggedInUser?.id)});
            router.canGoBack() ? router.back() : router.replace('/connect/conversations');
        }
    })

    const createNotificationMutation = useMutation({mutationFn: createNotification});
    const createMessageMutation = useMutation({
        mutationFn: createMessage,
        onMutate: (data) => {
            //Optimistically add the message in a pending state. The client generates the message id, so the inserted row will keep the same id.
            if(data?.details?.repliedToID && replyingToMessage){
                addNewMessageToQueryCache({...data.details, pending: true, failed: false, repliedTo: replyingToMessage})
                setReplyingToMessage(null);
            }
            else{
                addNewMessageToQueryCache({...data.details, pending: true, failed: false})
            }

            //Optimistically update the conversation's lastMessage in the conversations list
            //and move it to the top so the list reflects activity immediately.
            queryClient.setQueryData(QueryKeys.conversations.byUserId(loggedInUser?.id), old => {
                if(!old) return old;
                const idx = old.findIndex(c => c.id == conversationID);
                if(idx == -1) return old.map(c => c.id == conversationID ? {...c, lastMessage: {...data.details, Sender: loggedInUserRef.current}} : c);
                const updated = [...old];
                updated[idx] = {...updated[idx], lastMessage: {...data.details, Sender: loggedInUserRef.current}};
                updated.unshift(updated.splice(idx, 1)[0]);
                return updated;
            });
        },
        onError: (_error, variables) => {
            updateMessageInQueryCache({id: variables?.details?.id, pending: false, failed: true})
            Toast.show({type: 'error', text2: "Your message failed to send. Tap it to retry!"})
        },
        onSuccess: (data) => {
            const confirmedMessage = {...data, Sender: data?.Sender || loggedInUserRef.current, pending: false, failed: false};
            updateMessageInQueryCache(confirmedMessage)

            //Update the conversation's lastMessage with the confirmed DB record
            //and move it to the top of the list (matches the receiver-side reorder
            //in notificationHandler.js so both sides stay consistent).
            queryClient.setQueryData(QueryKeys.conversations.byUserId(loggedInUser?.id), old => {
                if(!old) return old;
                const idx = old.findIndex(c => c.id == conversationID);
                if(idx == -1) return old.map(c => c.id == conversationID ? {...c, lastMessage: confirmedMessage} : c);
                const updated = [...old];
                updated[idx] = {...updated[idx], lastMessage: confirmedMessage};
                updated.unshift(updated.splice(idx, 1)[0]);
                return updated;
            });

            //Only create notifications if the sender is an actual member of the conversation
            //(admins can view but should not trigger notifications for members)
            if(!isMember) return;

            const isGroup = conversation?.People?.length > 2 || conversation?.name != "*NONE*"
            const conversationName = conversation?.name == "*NONE*"
                ? isGroup
                    ? conversation?.People?.length > 3 ? conversation?.People?.slice(0, 3).map(p => p.firstName).join(", ") + " and " + (conversation?.People?.length - 3) + " others" : conversation?.People?.map(p => p.firstName).join(", ")
                    : loggedInUser?.firstName + " " + loggedInUser?.lastName
                : conversation?.name

            //Send a single template notification — the edge function resolves all
            //conversation members server-side (service role, no RLS) and fans out.
            //This ensures non-admin members who may be invisible to the sender via
            //People RLS still receive notifications.
            const mentionedPersonIDs = extractMentions(data.text)
                .filter(m => m.type === 'person')
                .map(m => m.id);

            const notificationDetails = [{
                type: "MESSAGE_RECEIVED",
                targetPersonID: null, //Edge function resolves recipients from PersonConversations
                path: `connect/conversations/${conversationID}`,
                message: data?.attachments?.length > 0 ? "Sent an attachment" : (isGroup ? loggedInUserRef.current?.firstName + " " + loggedInUserRef.current?.lastName + ": " + stripMentions(data.text) : stripMentions(data.text)),
                title: conversationName,
                parameters: { conversationID, senderID: loggedInUserRef.current?.id, messageID: data.id, contextName: conversationName, mentionedPersonIDs },
            }]

            createNotificationMutation.mutate(notificationDetails)
        }
    })

    const addNewMessageToQueryCache = (message, isLoggedInUser = true) => {
        queryClient.setQueryData(['messages', {conversationID}], old => {
            if(!old) return old;

            //Dedupe - if this message id is already in the cache (e.g. optimistic + broadcast), just merge it
            if(old.pages?.some(page => page?.data?.some(m => m.id == message.id))){
                return {...old, pages: old.pages.map(page => ({...page, data: page.data.map(m => m.id == message.id ? {...m, ...message, pending: message.pending ?? false, failed: message.failed ?? false} : m)}))}
            }

            const newMessage = {sentAt: new Date().toISOString(), ...message, Sender: isLoggedInUser ? loggedInUserRef.current : message.Sender};
            const pages = [...old.pages];
            pages[0] = {...pages[0], data: [newMessage, ...(pages[0]?.data || [])]};
            return {...old, pages}
        })
    }

    const updateMessageInQueryCache = useCallback((message) => {
        queryClient.setQueryData(['messages', {conversationID}], old => {
            if(!old) return old;
            return {...old, pages: old.pages.map(page => ({...page, data: page.data.map(o => o.id == message.id ? {...o, ...message} : o)}))}
        })
    }, [queryClient, conversationID])

    const retryMessage = useCallback((message) => {
        updateMessageInQueryCache({id: message.id, pending: true, failed: false})
        createMessageMutation.mutate({
            details: {
                id: message.id,
                senderID: message.senderID,
                conversationID: conversationID,
                text: message.text,
                attachments: message.attachments || [],
                repliedToID: message.repliedToID
            }
        })
    }, [updateMessageInQueryCache, createMessageMutation.mutate, conversationID])

    const addReactionToQueryCache = (reaction) => {
        queryClient.setQueryData(['messages', {conversationID}], old => {
            const newData = {...old};
            newData.pages = newData.pages.map(page => ({...page, data: page.data.map(o => o.id == reaction.messageID ? {...o, Reactions: [...o.Reactions, {...reaction, Person: loggedInUser}]} : o)}))
            return newData
        });
    }

    const removeReactionFromQueryCache = (reaction) => {
        queryClient.setQueryData(['messages', {conversationID}], old => {
            const newData = {...old};
            newData.pages = newData.pages.map(page => ({...page, data: page.data.map(o => o.id == reaction.messageID ? {...o, Reactions: [...(o.Reactions?.filter(r => !(r.messageID == reaction.messageID && r.personID == reaction.personID)) || [])]} : o)}))
            return newData
        })
    }

    const {rt} = useUnistyles();
    const inputBottomInset = isNative ? rt.insets.bottom : 20;

    //Shared value for the growing multiline input — updated by MessageInput's onLayout.
    //KeyboardChatScrollView uses this to extend the scrollable range so the last messages
    //aren't hidden under a taller input.
    const extraContentPadding = useSharedValue(0);

    //Stable renderScrollComponent reference — LegendList requires a stable callback.
    const renderScrollComponent = useCallback(
        (props) => <VirtualizedListScrollView {...props} extraContentPadding={extraContentPadding} />,
        [extraContentPadding]
    );


    // const messagesRef = useRef(null);

    //Memoized — the filter output feeds the avatars memo below and MessageInput's
    //members prop; a fresh array each render would defeat both.
    const peopleWithoutCurrentUser = useMemo(() => conversation?.People?.filter(p => p?.id != loggedInUser?.id), [conversation?.People, loggedInUser?.id]);
    const isRoleBased = conversation?.membersByGroup === true;
    const mutedForUser = !isRoleBased && conversation?.People?.find(p => p?.id == loggedInUser?.id)?.isMuted;
    const isMember = Array.isArray(conversation?.People) && conversation.People.some(p => p?.id == loggedInUser?.id);
    const showAdminBanner = !isMember && loggedInUser?.isAdmin && !adminBannerDismissed;
    const saveShortcut = useSaveShortcut(loggedInUser?.id);

    const avatars = useMemo(() => {
        if(conversation?.avatarPath){
            return [<Avatar key={conversationID} size={AVATAR_SIZE} profile={{id: conversationID, key: conversation?.avatarPath}} disableBorder directory="conversations/" />];
        }
        const list = peopleWithoutCurrentUser?.slice(0,5).map((p, i) => {
            return <View key={p.id} style={styles.avatarStackItem(AVATAR_STACK_OFFSET*i)}>
                <Avatar size={AVATAR_SIZE} profile={p} disableBorder />
            </View>
        }) || [];
        if(peopleWithoutCurrentUser?.length > 5){
            list.push(<View key="more" style={styles.avatarStackItem(AVATAR_STACK_OFFSET*5)}>
                <View style={styles.avatarMoreCircle(AVATAR_SIZE)}>
                    <Text style={styles.avatarMoreText(AVATAR_SIZE)}>+{peopleWithoutCurrentUser?.length - 5}</Text>
                </View>
            </View>)
        }
        return list;
    }, [conversation?.avatarPath, conversationID, peopleWithoutCurrentUser])

    //Any unread notification referencing this conversation clears when viewed (not just MESSAGE_RECEIVED)
    useMarkNotificationsRead({ conversationID });

    //Track recently visited conversation
    useEffect(() => {
        if(conversationID && conversation){
            const membersNames = peopleWithoutCurrentUser?.slice(0, 4)?.map((p, i) => {
                return i == 0
                    ? p.firstName + (peopleWithoutCurrentUser?.length == 1 ? ` ${p.lastName}` : "")
                    : i == peopleWithoutCurrentUser.length - 1
                        ? " & " + p.firstName
                        : (i == 3 ? " + " + (peopleWithoutCurrentUser?.length - 3) + " others" : ", " + p.firstName)
            }).join("")
            const conversationName = conversation?.name == "*NONE*" ? membersNames : conversation?.name;

            saveToRecents({
                id: conversationID,
                url: `/connect/conversations/${conversationID}`,
                title: conversationName || 'Conversation',
                params: { conversationID },
            });
        }
    }, [conversationID, conversation?.name])
    
    useEffect(() => {
        const membersNames = peopleWithoutCurrentUser?.slice(0, 4)?.map((p, i) => {
            return i == 0
                ? p.firstName + (peopleWithoutCurrentUser?.length == 1 ? ` ${p.lastName}` : "")
                : i == peopleWithoutCurrentUser.length - 1
                    ? " & " + p.firstName
                    : (i == 3 ? " + " + (peopleWithoutCurrentUser?.length - 3) + " others" : ", " + p.firstName)
        }).join("")

        const conversationName = conversation?.name == "*NONE*" ? membersNames : conversation?.name;
        const subtitle = isRoleBased ? conversation?.Roles?.map(r => r.name).join(", ") : (conversation?.name != "*NONE*" ? membersNames : null);

        navigation.setOptions({
            headerTitle: () => (
                <View style={styles.headerTitleContainer}>
                    <Text numberOfLines={1} style={styles.headerTitle}>{conversationName || "Conversation"}</Text>
                    {subtitle && (
                        <Text numberOfLines={1} style={styles.headerSubtitle}>{subtitle}</Text>
                    )}
                </View>
            ),
            headerRight: () => (
                <DropdownMenu
                    testID="conversation-header-menu"
                    triggerComponent={<View style={styles.headerMenuTrigger}><Unicon name="ellipsis-horizontal" size={22} colorVariant="text"/></View>}
                    options={[
                        {
                            name: 'Refresh',
                            icon: {android: 'refresh', ios: 'arrow.clockwise', web: 'refresh-outline'},
                            onSelect: () => refreshConversation()
                        },
                        {
                            name: 'Info',
                            icon: {android: 'info', ios: 'info.circle', web: 'information-circle-outline'},
                            onSelect: () => {
                                setTimeout(() => {
                                    router.push({pathname: 'connect/conversations/info', params: {conversationID}});
                                }, 200);
                            }
                        },
                        {
                            name: 'Save to Shortcuts',
                            icon: {android: 'add_shortcut', ios: 'bookmark', web: 'bookmark-outline'},
                            onSelect: () => saveShortcut({ screen: `/connect/conversations/${conversationID}`, label: conversationName || 'Conversation', icon: 'chatbubbles-outline' })
                        },
                        ...(!isRoleBased ? [{
                            name: mutedForUser ? 'Unmute Conversation' : 'Mute Conversation',
                            icon: mutedForUser
                                ? {android: 'notifications', ios: 'bell', web: 'notifications-outline'}
                                : {android: 'notifications_off', ios: 'bell.slash', web: 'notifications-off-outline'},
                            onSelect: () => {
                                setTimeout(() => {
                                    toggleMuteMutation.mutate({conversationID, personID: loggedInUser?.id, isMuted: !mutedForUser});
                                }, 200);
                            }
                        }] : []),
                        ...((loggedInUser?.id == conversation?.createdByID || loggedInUser?.isAdmin) ? [{
                            name: 'Edit',
                            icon: {android: 'edit', ios: 'pencil', web: 'create-outline'},
                            onSelect: () => {
                                setTimeout(() => {
                                    router.push({pathname: '/connect/conversations/create', params: {id: conversation?.id}});
                                }, 200);
                            }
                        }] : []),
                        ...(!isRoleBased && conversation?.People?.length > 2 ? [{
                            separator: true,
                            name: 'Leave Conversation',
                            destructive: true,
                            icon: {android: 'logout', ios: 'square.and.arrow.up', web: 'log-out'},
                            onSelect: () => {
                                showAlert({
                                    title: 'Leave Conversation',
                                    subtitle: 'This will remove you from the conversation but will not delete it for others. Your previous messages will still be visible to remaining members.',
                                    isDestructive: true,
                                    onConfirm: () => leaveConversationMutation.mutate({conversationID, personID: loggedInUser?.id}),
                                    confirmText: 'Leave'
                                });
                            }
                        }] : [])
                    ]}
                />
            ),
        })
    }, [avatars, conversation, saveShortcut, refreshConversation])

    useEffect(() => {
        if(isNative || !isFocused) return; // Native gets updates via push notifications (see notificationHandler.js)

        //Catch up on anything missed while the screen was unfocused
        queryClient.invalidateQueries({queryKey: QueryKeys.messages.byConversationId(conversationID)});

        let messagesChannel;
        let cancelled = false;

        const setRealtimeUpdates = async () => {
            await supabase.realtime.setAuth();
            if(cancelled) return;

            messagesChannel = supabase.channel(`Messages:${conversationID}`, {
                config: { private: true },
            })

            messagesChannel.on('broadcast', { event: 'INSERT' }, (broadcastMessage) => {
                const record = broadcastMessage?.payload?.record;
                if(record?.senderID != loggedInUserRef.current?.id){
                    const sender = conversationRef.current?.People?.find(p => p.id == record?.senderID);
                    addNewMessageToQueryCache({...record, Sender: record?.Sender || sender}, false)

                    //Update the conversation's lastMessage in the conversations list
                    queryClient.setQueryData(QueryKeys.conversations.byUserId(loggedInUserRef.current?.id), old => {
                        if(!old) return old;
                        const idx = old.findIndex(c => c.id == conversationID);
                        if(idx == -1) return old;
                        const newData = [...old];
                        newData[idx] = {...newData[idx], lastMessage: {...record, Sender: sender}};
                        newData.unshift(newData.splice(idx, 1)[0]);
                        return newData;
                    });
                }
            })
            .on('broadcast', { event: 'UPDATE' }, (broadcastMessage) => {
                if(broadcastMessage?.payload?.record?.senderID != loggedInUserRef.current?.id){
                    updateMessageInQueryCache(broadcastMessage?.payload?.record)
                }
            })
            .subscribe()
        }

        setRealtimeUpdates();

        return () => {
            cancelled = true;
            if(messagesChannel) supabase.removeChannel(messagesChannel);
        }
    }, [conversationID, isNative, isFocused])

    const renderItem = useCallback(({item, index}) => {
        //flatData is chronological ascending: lower index = older (visually above), higher index = newer (visually below).
        const above = flatData[index - 1]; //older message rendered above this one
        const below = flatData[index + 1]; //newer message rendered below this one

        const minute = (m) => m?.sentAt?.substring(0, 16); //ISO string through the minute
        const day = (m) => m?.sentAt ? new Date(m.sentAt).toLocaleDateString() : null;

        //Two messages "connect" when same sender AND same minute AND same day.
        const connectedAbove = !!above && above.Sender?.id == item.Sender?.id && minute(above) == minute(item) && day(above) == day(item);
        const connectedBelow = !!below && below.Sender?.id == item.Sender?.id && minute(below) == minute(item) && day(below) == day(item);

        const isFirstInGroup = !connectedAbove; //top of a visual group
        const isLastInGroup = !connectedBelow;  //bottom of a visual group

        //Show a date divider whenever the day changes from the message above (and always at the very top).
        const showNewDateLabel = !above || day(above) != day(item);

        return <View key={item.id} testID="chat-bubble" style={styles.chatBubbleWrapper(item.pending)}>
            <ChatBubble
                message={item}
                isCurrentUser={item.Sender?.id == loggedInUser?.id}
                showSender={isFirstInGroup && !!item.Sender && item.Sender.id != loggedInUser?.id}
                showTime={isFirstInGroup}
                showNewDateLabel={showNewDateLabel}
                isFirstInGroup={isFirstInGroup}
                isLastInGroup={isLastInGroup}
                canInteract={isMember || loggedInUser?.isAdmin}
                canReact={isMember}
            />
            {item.failed &&
                <Pressable testID="retry-message" onPress={() => retryMessage(item)} style={styles.failedContainer}>
                    <Unicon name="alert-circle" size={14} colorVariant="error" />
                    <Text style={styles.failedText}>Failed to send. Tap to retry.</Text>
                </Pressable>
            }
        </View>
    }, [flatData, loggedInUser, isMember, retryMessage])

    //Guard after all hooks — react-compiler requires hooks before any early return
    const canViewConversation = loggedInUser?.can?.("View Conversations") && !!conversationID;
    const conversationExists = !!conversation && !Array.isArray(conversation);

    useEffect(() => {
        //Wait for both queries before judging — on cold start (e.g. push tap deep link)
        //loggedInUser/conversation are still undefined while the persisted cache hydrates
        //and the query fetches, and redirecting here would pop the deep-linked screen.
        if(loggedInUserLoading || conversationLoading) return;
        if(!canViewConversation || !conversationExists){
            router.canGoBack() ? router.back() : router.replace("/");
        }
    }, [loggedInUserLoading, canViewConversation, conversationLoading, conversationExists])

    if(!canViewConversation || (!conversationLoading && !conversationExists)){
        return null;
    }

    const updateReactions = (message, name) => {
        const currentUserReaction = message?.Reactions?.find(r => r.Person?.id == loggedInUser?.id)

        if(currentUserReaction){
            removeReactionMutation.mutate({messageID: message.id, personID: loggedInUser?.id})
        }

        if(currentUserReaction?.reaction != name){
            addReactionMutation.mutate({messageID: message.id, reaction: name, personID: loggedInUser?.id})
        }
    }

    return (
        <SplitView sidebar={<ConversationsList selectedConversationID={conversationID} />}>
        <View style={styles.screen}>
            {showAdminBanner && (
                <AdminViewingBanner
                    message="You're not in this conversation. You're viewing as an administrator for transparency."
                    topInset={100}
                    onDismiss={() => setAdminBannerDismissed(true)}
                />
            )}
            <KeyboardGestureArea
                interpolator="ios"
                style={[styles.listContainer, showAdminBanner && {paddingTop: 0}, !isNative && {flex: 1, minHeight: 0, overflow: 'hidden'}]}
                textInputNativeID="chat-input"
           >
                <LegendList
                    renderScrollComponent={renderScrollComponent}
                    ref={listRef}
                    recycleItems={isNative}
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps="always"
                    getEstimatedItemSize={(index, item) => {
                        return item?.attachments?.length > 0 ? 200 : 60;
                    }}
                    alignItemsAtEnd
                    maintainScrollAtEnd={isNative && !isScrollingToMessage}
                    maintainScrollAtEndThreshold={0.2}
                    maintainVisibleContentPosition={isNative && !isScrollingToMessage && (flatData?.length || 0) > 10}
                    keyExtractor={(item) => item.id}
                    data={listData}
                    renderItem={renderItem}
                    onScrollToIndexFailed={({index, highestMeasuredFrameIndex}) => {
                        listRef.current?.scrollToIndex?.({index: highestMeasuredFrameIndex, animated: false});
                        setTimeout(() => {
                            listRef.current?.scrollToIndex?.({index, animated: false, viewPosition: 0.5});
                        }, 300);
                    }}
                    contentContainerStyle={[styles.content, !isMember && {paddingBottom: rt.insets.bottom + 20}]}
                    style={[styles.legendListStyle(isReady), !isNative && {flex: 1, minHeight: 0, overflow: 'hidden'}]}
                    ListEmptyComponent={!fetchingMessages && isReady ? (
                        <EmptyListPlaceholder icon="chatbubbles-outline" message="No messages yet. Say something!" />
                    ) : null}
                    onStartReachedThreshold={1}
                    onStartReached={() => {
                        if(!isFetchingNextPage && hasNextPage) fetchNextPage()
                    }}
                />
                {!isReady && (
                    <View style={styles.loadingOverlay}>
                        <ActivityIndicator size="large"/>
                    </View>
                )}
                {isScrollingToMessage && (
                    <View style={styles.loadingOverlay}>
                        <ActivityIndicator size="large"/>
                    </View>
                )}

                {isMember && (
                    <KeyboardStickyView style={!isNative && {flexShrink: 0}}>
                        <MessageInput
                            nativeID="chat-input"
                            detection={["mention", "link"]}
                            members={peopleWithoutCurrentUser}
                            enableAttachments
                            hideReplyBanner
                            bottomInset={inputBottomInset}
                            extraContentPadding={extraContentPadding}
                            attachmentDirectory={"messageAttachments/" + conversationID}
                            onSendMessage={(text, attachments) => createMessageMutation.mutate({
                                details: {
                                    id: Crypto.randomUUID(),
                                    senderID: loggedInUser?.id,
                                    conversationID: conversationID,
                                    text: text,
                                    attachments: attachments
                                }
                            })}
                        />
                        <ReplyContainer
                            conversationID={conversationID}
                            members={peopleWithoutCurrentUser}
                            bottomInset={inputBottomInset}
                            onSendMessage={(text, attachments) => createMessageMutation.mutate({
                                details: {
                                    id: Crypto.randomUUID(),
                                    senderID: loggedInUser?.id,
                                    conversationID: conversationID,
                                    text: text,
                                    attachments: attachments,
                                    repliedToID: replyingToMessage?.id
                                }
                            })}
                        />
                        <EditContainer
                            conversationID={conversationID}
                            members={peopleWithoutCurrentUser}
                            bottomInset={inputBottomInset}
                            onEditMessage={(text, attachments) => {
                                const msg = editingMessage;
                                if(!msg?.id) return;
                                updateMessageMutation.mutate({
                                    details: {
                                        ...msg,
                                        text: text,
                                        attachments: attachments,
                                    }
                                })
                            }}
                        />
                    </KeyboardStickyView>
                )}

                {(isMember || loggedInUser?.isAdmin) && (
                    <ConnectedReactionMenu
                        selectionType="message"
                        canReact={isMember}
                        canReply={isMember}
                        canDelete={!isMember && loggedInUser?.isAdmin}
                        onReactionPress={(message, name) => {
                            updateReactions(message, name)
                        }}
                        onReply={(message) => {
                            setReplyingToMessage(message)
                        }}
                        onDelete={(message) => {
                            updateMessageMutation.mutate({details:{...message, isDeleted: true}})
                        }}
                        onEdit={(message) => {
                            setEditingMessage(message)
                        }}
                    />
                )}
            </KeyboardGestureArea>
        </View>
        </SplitView>
    )
}


 const styles = StyleSheet.create(({colors, fonts}, rt) => ({
        screen: {
            flex: 1,
            maxWidth: 700,
            width: "100%",
            alignSelf: "center",
            position: "relative",
        },
        listContainer: {
            flex: 1,
            paddingBottom: rt.isPortrait ? 0 : 100,
            paddingTop:100
        },
        content: {
            paddingTop: 0
        },
        failedContainer: {
            flexDirection:"row",
            alignItems:"center",
            gap:4,
            marginTop:4,
            alignSelf:"flex-end"
        },
        failedText: {
            color: colors.error,
            fontSize: 12
        },
        loadingOverlay: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: colors.background,
            alignItems: "center",
            justifyContent: "center"
        },
        avatarStackItem: (offset) => ({
            transform: [{translateX: offset}],
            position:"absolute"
        }),
        avatarMoreCircle: (AVATAR_SIZE) => ({
            borderRadius:AVATAR_SIZE/2,
            width:AVATAR_SIZE,
            height:AVATAR_SIZE,
            overflow:"hidden",
            borderColor:colors.background,
            borderWidth:2,
            backgroundColor:colors.surface,
            alignItems:"center",
            justifyContent:"center"
        }),
        avatarMoreText: (AVATAR_SIZE) => ({
            color:colors.text,
            fontFamily:fonts.bold,
            fontSize:14,
            textAlign:"center",
            lineHeight:AVATAR_SIZE - 2,
            marginLeft:-2
        }),
        headerTitleContainer: {
            alignItems: "center"
        },
        headerTitle: {
            color: colors.text,
            fontFamily: fonts.heading,
            fontSize: 16
        },
        headerSubtitle: {
            color: colors.textSecondary,
            fontStyle: "italic",
            fontSize: 12
        },
        headerMenuTrigger: {
            padding: 8,
            alignItems: 'center',
            justifyContent: 'center',
        },
        chatBubbleWrapper: (isPending) => ({
            opacity: isPending ? 0.5 : 1
        }),
        legendListStyle: (ready) => ({
            opacity: ready ? 1 : 0,
            maxWidth: 700,
            width: "100%",
        })
    }))

export default MessagesScreen;
